import React, { useState, useEffect } from 'react';
import { websearchService } from '../../services/websearchService';
import { MessageSquare } from 'lucide-react';

const EngagementSidebar = ({ item, bookId, user, onClose, onUpdate, isDarkMode }) => {
  const [newNote, setNewNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  
    useEffect(() => {
      loadNotes();
    }, [item.id]);
  
    const loadNotes = async () => {
      try {
        const itemNotes = await websearchService.getNotes(bookId, item.id);
        setNotes(itemNotes);
      } catch (error) {
        console.error('Error loading notes:', error);
      }
    };

  const handleAddNote = async () => {
      if (!newNote.trim()) return;
  
      setLoading(true);
      try {
        await websearchService.addNote(bookId, item.id, {
          content: newNote,
          createdBy: user.uid // Replace with actual user
        });
        setNewNote('');
        await loadNotes();
      } catch (error) {
        console.error('Error adding note:', error);
      } finally {
        setLoading(false);
      }
    };

  const handleDeleteNote = (noteId) => {
    const updatedItem = {
      ...item,
      notes: (item.notes || []).filter((n) => n.id !== noteId),
    };
    onUpdate(updatedItem);
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-gray-50';
  const textClass = isDarkMode ? 'text-slate-100' : 'text-gray-900';
  const borderClass = isDarkMode ? 'border-slate-700' : 'border-gray-200';

  const getItemName = (item) => item?.name || item?.title || 'Unknown Item';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Sidebar Panel */}
      <div className="relative w-full max-w-md bg-slate-900 border-l border-slate-800 h-full shadow-2xl flex flex-col animate-slide-in-right">

        {/* Header */}
        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950">
          <div className={`p-4 border-b ${borderClass}`}>
            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {item.name || item.title}
            </div>
              {item.company && (
                <div className={`text-sm ${textClass}`}>{item.company}</div>
              )}
            </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors p-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Notes list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-900">
          
            {notes.map((note) => (
              <div
                key={note.id}
                className="relative pl-6 border-l-2 border-slate-700 pb-2 group"
              >
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-slate-900 border-2 border-indigo-500"></div>

                <div className="mb-1 flex justify-between items-start">
                  <div className={`text-xs ${textClass} mt-2`}>
                    {new Date(note.createdAt?.seconds * 1000).toLocaleDateString()}
                </div>

                  <button
                    onClick={() => handleDeleteNote(note.id)}
                    className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete note"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      className="w-3 h-3"
                    >
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>

                <div className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded-lg border border-slate-800 whitespace-pre-wrap">
                  {note.content}
                </div>
              </div>
            ))}

            {notes.length === 0 && (
            <div className={`text-center py-8 ${textClass}`}>
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notes yet</p>
            </div>
          )}
        </div>

        {/* Input Form */}
        <div className="p-6 border-t border-slate-800 bg-slate-950">
          <form onSubmit={handleAddNote}>
            <label className="block text-xs font-medium text-slate-400 mb-2">
              Add New Update
            </label>

            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              placeholder="Logged call, sent email, or added a note..."
              className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none placeholder-slate-600"
            ></textarea>

            <div className="mt-3 flex justify-end">
              <button
                type="submit"
                disabled={!newNote.trim()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Post Update
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default EngagementSidebar;
